import { apiFetch } from '@/lib/api/client';
import { env } from '@/lib/env';
import { SEED_SITES } from '@/lib/mock/db';
import type { Site } from '@/lib/types';
import type {
  MaterialDashboardResponse,
  MaterialInventoryLine,
  MaterialKindTotal,
  MaterialLine,
} from '@/pages/office/queries';
import type { SiteContactBook } from '@/pages/sites/contacts/queries';
import type { PmisSiteContact } from '@/pages/sites/pmis/queries';
import type {
  OfficeDashboardResponse,
  OfficeDashboardRow,
  OfficeDashboardTotals,
  OfficeTrendPoint,
} from '@/pages/sites/pmis/office/queries';

/**
 * 현장 전체 대시보드의 <b>개발계 목 데이터</b>(사용자 지시 2026-08-31).
 *
 * <h2>왜 필요한가</h2>
 * 개요·매출·자재·연락처는 전부 <b>현장이 자기 화면에 넣은 것</b>을 더한 표다. 개발계 DB 에는 그
 * 입력이 없어 네 탭이 모두 「자료 없음」 이었고, 그러면 <b>화면을 고쳐도 고친 것이 안 보인다</b> —
 * 표 폭·정렬·색·빈칸 처리는 값이 있어야 판단이 된다.
 *
 * <h2>규칙 셋</h2>
 * <ol>
 *   <li><b>개발 빌드에서만</b> 돈다({@code env.isDev}). 운영에서는 {@link withDevMock} 이 첫 줄에서
 *       그대로 서버 응답을 돌려주므로 이 파일의 함수는 <b>한 번도 불리지 않는다</b>.</li>
 *   <li><b>서버가 한 줄이라도 주면 그것을 쓴다.</b> 목은 «비었을 때만» 나선다 — 진짜 값 위에
 *       가짜를 덮으면 개발계에서 본 숫자를 아무도 못 믿는다.</li>
 *   <li><b>현장 이름은 서버 것을 쓴다.</b> `/v1/sites` 로 실제 현장을 받아 그 위에 숫자만 지어낸다.
 *       현장 목록만 진짜인 개발계에서 대시보드가 다른 이름을 부르면 개요의 현장 줄과 자재 줄이
 *       서로 다른 회사 것이 된다. 그것마저 못 받으면 시드 현장으로 물러선다.</li>
 * </ol>
 *
 * <h2>숫자는 <b>현장 id 에서</b> 나온다 — 난수가 아니다</h2>
 * {@code Math.random()} 을 쓰면 새로고침마다 매출이 바뀌어, 화면을 고치는 사람이 자기 수정 탓인지
 * 목 탓인지 가릴 수 없다. 그래서 현장 id 를 해시해 값을 뽑는다 — 같은 현장은 늘 같은 숫자다.
 */

// ── 씨앗 ────────────────────────────────────────────────────────────────────

/** FNV-1a. 암호용이 아니라 <b>같은 입력에 같은 숫자</b>가 필요할 뿐이다. */
function hash(text: string): number {
  let value = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    value ^= text.charCodeAt(index);
    value = Math.imul(value, 0x01000193) >>> 0;
  }
  return value;
}

/** `key` 로 갈래를 나눈 0~1 값. 한 현장에서 매출과 원가율이 같은 값이 되지 않게 한다. */
function seed(site: Site, key: string): number {
  return (hash(`${site.id}:${key}`) % 10_000) / 10_000;
}

/** 씨앗을 범위로 편다. */
function between(site: Site, key: string, min: number, max: number): number {
  return min + seed(site, key) * (max - min);
}

const round = (value: number, digits = 0): number => {
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
};

/** 상태 필터는 서버와 같은 뜻으로 건다 — `all` 이면 전부. */
function filterSites(sites: Site[], status: string): Site[] {
  return status === 'all' ? sites : sites.filter((site) => site.status === status);
}

/**
 * 대시보드가 딛고 설 현장 목록. 서버 → 시드 순으로 물러선다.
 *
 * <p>준공·미진행까지 다 담는다. 거르는 일은 부르는 쪽이 상태 필터로 한다.
 */
async function loadSites(): Promise<Site[]> {
  const fromServer = await apiFetch<Site[]>('/v1/sites').catch(() => [] as Site[]);
  const sites = fromServer.length > 0 ? fromServer : SEED_SITES;
  return [...sites].sort((a, b) => a.sortOrder - b.sortOrder);
}

// ── 매출·원가·수금 ──────────────────────────────────────────────────────────

/** 도급액(원). 연면적이 있으면 그것에 비례시킨다 — 큰 현장이 큰 숫자를 갖는 편이 눈에 자연스럽다. */
function contractOf(site: Site): number {
  const base = site.grossAreaSqm ? site.grossAreaSqm * between(site, 'unit', 1_600_000, 2_400_000) : between(site, 'flat', 6_000_000_000, 90_000_000_000);
  return round(base / 100_000_000) * 100_000_000;
}

function officeRow(site: Site, mode: 'month' | 'year'): OfficeDashboardRow {
  const common = {
    siteId: site.id,
    siteName: site.name,
    siteStatus: site.status,
    siteCategory: site.siteCategory,
  };

  /*
    미진행 현장은 <b>비워 둔다</b>. 계약 전 현장에 매출이 찍혀 있으면 그 표는 한 번에 거짓말이
    되고, 화면이 «값 없음»(—)을 어떻게 그리는지도 개발계에서 확인해야 한다.
  */
  if (site.status === '미진행') {
    return {
      ...common,
      ready: false,
      hasData: false,
      contractExVat: null,
      contractIncVat: null,
      targetCostRate: null,
      periodRevenue: null,
      periodCost: null,
      periodCollect: null,
      cumPlanProgress: null,
      cumProgress: null,
      cumRevenue: null,
      cumCost: null,
      cumCollect: null,
      receivable: null,
      costRate: null,
      collectRate: null,
      lastConfirmedYm: null,
      overdueCount: 0,
    };
  }

  const contract = contractOf(site);
  const done = site.status === '준공' ? 100 : round(between(site, 'progress', 18, 96));
  const plan = site.status === '준공' ? 100 : Math.min(100, round(done + between(site, 'plan', -9, 7)));
  const costRate = round(between(site, 'costRate', 84, 103), 1);
  const collectRate = round(between(site, 'collectRate', 55, 99), 1);

  const cumRevenue = round((contract * done) / 100);
  const cumCost = round((cumRevenue * costRate) / 100);
  const cumCollect = round((cumRevenue * collectRate) / 100);

  // 연 조회는 한 해치, 월 조회는 한 달치다 — 두 숫자가 같으면 기간 필터가 안 걸린 것처럼 보인다.
  const share = mode === 'year' ? between(site, 'yearShare', 0.16, 0.34) : between(site, 'monthShare', 0.012, 0.052);
  const periodRevenue = site.status === '준공' ? 0 : round(contract * share);
  const periodCost = round((periodRevenue * costRate) / 100);
  const periodCollect = round(periodRevenue * between(site, 'periodCollect', 0.45, 1.05));

  return {
    ...common,
    ready: true,
    hasData: true,
    contractExVat: contract,
    contractIncVat: round(contract * 1.1),
    targetCostRate: round(between(site, 'target', 87, 93), 1),
    periodRevenue,
    periodCost,
    periodCollect,
    cumPlanProgress: plan,
    cumProgress: done,
    cumRevenue,
    cumCost,
    cumCollect,
    receivable: cumRevenue - cumCollect,
    costRate,
    collectRate,
    lastConfirmedYm: null,
    // 마감 지연은 <b>몇 곳만</b>이다. 전부 밀려 있으면 개요의 「주요 현장」이 전 현장 목록이 된다.
    overdueCount: seed(site, 'overdue') > 0.75 ? 1 + Math.round(seed(site, 'overdueN')) : 0,
  };
}

function sum(rows: OfficeDashboardRow[], pick: (row: OfficeDashboardRow) => number | null): number {
  return rows.reduce((total, row) => total + (pick(row) ?? 0), 0);
}

function rate(numerator: number, denominator: number): number | null {
  return denominator === 0 ? null : round((numerator / denominator) * 100, 1);
}

export async function mockOfficeDashboard(
  mode: 'month' | 'year',
  year: number,
  month: number,
  status: string,
): Promise<OfficeDashboardResponse> {
  const sites = filterSites(await loadSites(), status);
  const rows = sites.map((site) => officeRow(site, mode));

  const periodRevenue = sum(rows, (row) => row.periodRevenue);
  const periodCost = sum(rows, (row) => row.periodCost);
  const cumRevenue = sum(rows, (row) => row.cumRevenue);
  const cumCost = sum(rows, (row) => row.cumCost);
  const cumCollect = sum(rows, (row) => row.cumCollect);

  const totals: OfficeDashboardTotals = {
    siteCount: rows.length,
    readyCount: rows.filter((row) => row.ready).length,
    overdueSites: rows.filter((row) => row.overdueCount > 0).length,
    contractExVat: sum(rows, (row) => row.contractExVat),
    periodRevenue,
    periodCost,
    periodProfit: periodRevenue - periodCost,
    periodCollect: sum(rows, (row) => row.periodCollect),
    cumRevenue,
    cumCost,
    receivable: cumRevenue - cumCollect,
    costRate: rate(cumCost, cumRevenue),
    profitRate: rate(periodRevenue - periodCost, periodRevenue),
  };

  return { mode, year, month: mode === 'month' ? month : null, rows, totals };
}

/** 연 조회에서 그리는 월별 추이. 12개월이 다 있어야 꺾은선이 끊기지 않는다. */
export async function mockOfficeTrend(year: number): Promise<OfficeTrendPoint[]> {
  const sites = (await loadSites()).filter((site) => site.status !== '미진행');
  return Array.from({ length: 12 }, (_, index) => {
    const month = index + 1;
    const key = `trend-${year}-${month}`;
    const revenue = sites.reduce((total, site) => total + contractOf(site) * between(site, key, 0.008, 0.045), 0);
    return {
      periodYm: `${year}-${String(month).padStart(2, '0')}`,
      revenue: round(revenue),
      cost: round(revenue * 0.91),
      collect: round(revenue * 0.78),
    };
  });
}

// ── 주요자재 ────────────────────────────────────────────────────────────────

/**
 * 개발계 자재는 <b>레미콘·철근 둘</b>이다(사용자 지시 2026-08-31).
 *
 * <p>서버의 {@code MaterialCodes} 에는 거푸집·데크·기타도 있고 {@code KIND_ORDER} 가 레미콘·철근을
 * 앞에 세운다. 한때 거푸집을 <b>절반의 현장에만</b> 넣어 「자재를 안 쓰는 현장은 줄이 없다」 는
 * 규칙을 개발계에서 눈으로 보게 했는데, 그 값어치보다 <b>주요자재가 무엇인지 흐려지는 손해</b>가
 * 크다(사용자 지적). 목이 실제 현장의 자재 구성을 대신 정하는 셈이 되기 때문이다.
 *
 * <p>「줄 없는 현장」은 준공·미진행 현장이 이미 만들어 준다 — 그쪽은 아래에서 통째로 뺀다.
 */
const KINDS: { key: string; label: string; unit: string }[] = [
  { key: 'CONCRETE', label: '레미콘', unit: '㎥' },
  { key: 'REBAR', label: '철근', unit: 'ton' },
];

function materialLine(site: Site, kind: (typeof KINDS)[number], mode: 'month' | 'year'): MaterialLine {
  const scale = site.grossAreaSqm ?? 20_000;
  // 연면적당 물량 — 레미콘 ㎥, 철근 ton. RC 건물의 어림수라 자릿수가 실제와 비슷하게 나온다.
  const perArea = kind.key === 'CONCRETE' ? between(site, `${kind.key}-b`, 0.35, 0.55) : between(site, `${kind.key}-b`, 0.045, 0.075);
  const baseline = round(scale * perArea, kind.key === 'REBAR' ? 1 : 0);

  const deliveredRate = round(between(site, `${kind.key}-d`, 22, 98), 1);
  // 사용이 반입을 넘을 수는 없다 — 넘겨 두면 소진율 색이 늘 빨갛고, 그 색이 뜻을 잃는다.
  const usageRate = round(deliveredRate * between(site, `${kind.key}-u`, 0.7, 0.99), 1);
  const digits = kind.key === 'REBAR' ? 1 : 0;
  const delivered = round((baseline * deliveredRate) / 100, digits);
  const used = round((baseline * usageRate) / 100, digits);
  const periodShare = mode === 'year' ? between(site, `${kind.key}-py`, 0.18, 0.4) : between(site, `${kind.key}-pm`, 0.02, 0.09);

  return {
    siteId: site.id,
    siteName: site.name,
    siteStatus: site.status,
    materialKind: kind.key,
    materialKindLabel: kind.label,
    unit: kind.unit,
    itemCount: 2 + Math.round(seed(site, `${kind.key}-items`) * 6),
    baselineQty: baseline,
    plannedQty: round(baseline * between(site, `${kind.key}-p`, 0.9, 1.05), digits),
    deliveredQty: delivered,
    usedQty: used,
    periodDeliveredQty: round(delivered * periodShare, digits),
    periodUsedQty: round(used * periodShare, digits),
    deliveredRate,
    usageRate,
    lastPeriodYm: null,
  };
}

function kindTotal(kind: (typeof KINDS)[number], lines: MaterialLine[]): MaterialKindTotal {
  const mine = lines.filter((line) => line.materialKind === kind.key);
  const total = (pick: (line: MaterialLine) => number | null) =>
    mine.reduce((acc, line) => acc + (pick(line) ?? 0), 0);
  const baselineQty = round(total((line) => line.baselineQty), 1);
  const deliveredQty = round(total((line) => line.deliveredQty), 1);
  const usedQty = round(total((line) => line.usedQty), 1);
  return {
    materialKind: kind.key,
    materialKindLabel: kind.label,
    unit: kind.unit,
    aggregatable: true,
    siteCount: mine.length,
    itemCount: total((line) => line.itemCount),
    baselineQty,
    deliveredQty,
    usedQty,
    // 비율은 <b>합에서 다시 낸다</b> — 현장별 비율의 평균을 내면 큰 현장과 작은 현장이 같은 무게가 된다.
    deliveredRate: rate(deliveredQty, baselineQty),
    usageRate: rate(usedQty, baselineQty),
  };
}

export async function mockMaterialDashboard(
  mode: 'month' | 'year',
  year: number,
  month: number,
  status: string,
): Promise<MaterialDashboardResponse> {
  // 미진행 현장에는 자재가 없다 — 첫 반입이 곧 착공이다.
  const sites = filterSites(await loadSites(), status).filter((site) => site.status !== '미진행');

  const materials = sites.flatMap((site) =>
    KINDS.map((kind) => materialLine(site, kind, mode)),
  );

  const inventory: MaterialInventoryLine[] = sites
    .filter((site) => seed(site, 'inventory') > 0.3)
    .map((site) => ({
      siteId: site.id,
      siteName: site.name,
      siteStatus: site.status,
      lineCount: 6 + Math.round(seed(site, 'inv-line') * 24),
      unverifiedCount: Math.round(seed(site, 'inv-unverified') * 4),
      shortageCount: Math.round(seed(site, 'inv-shortage') * 5),
      repairCount: Math.round(seed(site, 'inv-repair') * 3),
      lostCount: Math.round(seed(site, 'inv-lost') * 2),
      disposedCount: Math.round(seed(site, 'inv-disposed') * 2),
      lastSurveyDate: null,
    }));

  return {
    mode,
    year,
    month: mode === 'month' ? month : null,
    canSeeMaterial: true,
    canSeeInventory: true,
    materials,
    kindTotals: KINDS.map((kind) => kindTotal(kind, materials)).filter((total) => total.siteCount > 0),
    // 잡자재 신청은 화면이 접어 두었다({@code MaterialOverviewTab} 의 `SHOW_REQUESTS`) — 빈 채로 둔다.
    requests: [],
    totals: {
      materialSiteCount: new Set(materials.map((line) => line.siteId)).size,
      materialItemCount: materials.reduce((total, line) => total + line.itemCount, 0),
      requestSiteCount: 0,
      requestAmount: null,
      unsettledCount: 0,
      unsettledAmount: null,
      inventorySiteCount: inventory.length,
      inventoryLineCount: inventory.reduce((total, line) => total + line.lineCount, 0),
      inventoryUnverifiedCount: inventory.reduce((total, line) => total + line.unverifiedCount, 0),
    },
    inventory,
  };
}

// ── 연락처 ──────────────────────────────────────────────────────────────────

const FAMILY = ['김', '이', '박', '최', '정', '강', '조', '윤', '장', '임'];
const GIVEN = ['도현', '서준', '민수', '유진', '한결', '지우', '세영', '가람', '현우', '수빈', '태윤', '나윤'];
const RANKS = ['부장', '차장', '과장', '대리', '사원'];
const VENDORS = ['대성건설', '한백전기', '동아설비', '삼진철근', '우성레미콘', '태산가설'];

/** 사람 이름 하나 — 현장 id + 자리 이름으로 뽑아 늘 같은 사람이 같은 자리에 선다. */
function personName(site: Site, slot: string): string {
  const key = hash(`${site.id}:${slot}`);
  return `${FAMILY[key % FAMILY.length]}${GIVEN[(key >> 3) % GIVEN.length]}`;
}

function phone(site: Site, slot: string): string {
  const key = hash(`${site.id}:${slot}:phone`);
  return `010-${String((key % 9000) + 1000)}-${String(((key >> 7) % 9000) + 1000)}`;
}

function contact(site: Site, slot: string, group: string, company: string | null, duty: string): PmisSiteContact {
  const key = hash(`${site.id}:${slot}:rank`);
  return {
    id: null,
    personnelId: null,
    department: null,
    group,
    company,
    name: personName(site, slot),
    rank: RANKS[key % RANKS.length] ?? '과장',
    duty,
    phone: phone(site, slot),
    extension: null,
    email: null,
    note: null,
    // 목이라도 <b>고칠 수 없는 줄</b>로 둔다. 개발계에서 눌러 고치면 서버가 없어 그 자리에서 실패한다.
    editable: false,
  };
}

/**
 * 전 현장 연락처 — 현장마다 <b>다섯 자리</b>를 채운다.
 *
 * <p>발주처·감리·현장대리인·공무·협력업체. 이 탭의 값은 「현장을 모르는 채로 사람을 찾는 것」이라,
 * 한 현장에 한 명만 있으면 검색이 무엇을 걸러 내는지 개발계에서 확인이 안 된다.
 */
export async function mockSiteContacts(): Promise<SiteContactBook[]> {
  const sites = await loadSites();
  return sites.map((site) => {
    const vendor = VENDORS[hash(site.id) % VENDORS.length] ?? '대성건설';
    return {
      siteId: site.id,
      siteName: site.name,
      siteStatus: site.status,
      siteCategory: site.siteCategory,
      contacts: [
        contact(site, 'client', '발주처', site.clientName ?? '발주처', '공사담당'),
        contact(site, 'supervisor', '감리', site.supervisorName ?? '한국건축감리', '책임감리원'),
        contact(site, 'manager', '당사', '보미건설', '현장대리인'),
        contact(site, 'admin', '당사', '보미건설', '공무담당'),
        contact(site, 'vendor', '협력업체', vendor, '자재·배차'),
      ],
    };
  });
}

// ── 호출부가 쓰는 문 ────────────────────────────────────────────────────────

/**
 * 서버를 먼저 부르고, <b>개발계에서 비었을 때만</b> 목으로 채운다.
 *
 * <p>{@code env.isDev} 가 아니면 이 함수는 `load()` 를 그대로 돌려주는 껍데기다 — 운영에서
 * 목이 새어 나갈 길이 없다. 실패를 삼키기 때문에 무엇이 일어났는지 콘솔에 한 줄 남긴다:
 * 개발계에서 403 이 나도 화면이 그려지므로, 적지 않으면 권한 문제를 영영 모르고 지나간다.
 */
export async function withDevMock<T>(
  label: string,
  load: () => Promise<T>,
  isEmpty: (data: T) => boolean,
  build: () => Promise<T>,
): Promise<T> {
  if (!env.isDev) return load();
  try {
    const data = await load();
    if (!isEmpty(data)) return data;
    console.info(`[dev-mock] ${label} — 서버가 빈 결과를 주어 목 데이터로 채웁니다.`);
  } catch (error) {
    console.info(`[dev-mock] ${label} — 불러오지 못해 목 데이터로 채웁니다.`, error);
  }
  return build();
}
